import { getLocaleSnapshot } from '@/lib/i18n/runtime';
import { sessionRevision, requireCurrentSession } from '@/features/auth/session-scope';
import { client } from '@blazeteams/sdk/client';
import { useAuthStore } from '@/store/auth-store';
import { API_BASE_URL } from '@/api/api-config';
import { sessionAPIBase, captureSessionRequest, requireSessionRequest, renewSession, type SessionRequest } from './session-renewal';
import {
  getConversationForbiddenDetail,
  INBOX_CONVERSATION_FORBIDDEN_EVENT,
} from '@/features/inbox/conversation-access';

let initialized = false;

const requestSessions = new WeakMap<Request, number>();
const ownedRequests = new WeakMap<Request, SessionRequest>();
const publicRequests = new WeakSet<Request>();

// Match the API pathname, never a redirect/query containing "/public/".
function publicAPI(url: string, rawBase: unknown): boolean {
  const baseUrl = typeof rawBase === 'string' ? rawBase : client.getConfig().baseUrl ?? API_BASE_URL;
  const base = new URL(baseUrl, new URL(url).origin).pathname.replace(/\/+$/, '');
  return new URL(url).pathname.startsWith(base + '/public/');
}

export function initApiClient() {
  if (initialized) return;
  initialized = true;

  // Canonical `/api/v1` base from the single config point — never bare `/api`.
  const baseUrl = API_BASE_URL;

  client.setConfig({ baseUrl });

  // Inject Authorization on configured API requests via an interceptor instead of
  // the per-endpoint `auth` callback. Reason: `auth` only fires for endpoints
  // whose OpenAPI spec declares `security: [{ type: 'apiKey', ... }]`. The
  // swagger upstream is incomplete for several endpoints (e.g. POST /bots/flows
  // has no `security` block) so those calls go out without an Authorization
  // header and the backend returns 401 "Missing authorization header". Doing it
  // here covers those endpoints while preserving explicitly supplied login
  // and workspace-choice credentials.
  client.interceptors.request.use((request, options) => {
    requestSessions.set(request, sessionRevision());
    // Explicit user language also applies to server-generated UI messages.
    // Caller-provided locale remains authoritative for specialized requests.
    if (!request.headers.has('Accept-Language')) request.headers.set('Accept-Language', getLocaleSnapshot().locale);
    if (publicAPI(request.url, options.baseUrl)) {
      publicRequests.add(request);
      return request;
    }
    const base = sessionAPIBase(request, options.baseUrl);
    const token = useAuthStore.getState().token;
    // Inject only on the configured API. Explicit capabilities remain unchanged.
    if (base && token && !request.headers.has('Authorization')) {
      request.headers.set('Authorization', token.startsWith('Bearer ') ? token : 'Bearer ' + token);
    }
    if (base) {
      const owner = captureSessionRequest(request, base);
      if (owner) ownedRequests.set(request, owner);
    }
    return request;
  });

  // Only current owned API credentials renew. A successful renewal can repeat
  // ordinary GET/HEAD once; writes/generative GETs keep their original result. Transient/protocol failures
  // retain session/drafts; native refusal returns to login without clearing prefs.
  client.interceptors.response.use(async (response, request, options) => {
    if (publicRequests.has(request)) {
      request.signal.throwIfAborted();
      return response;
    }
    const revision = requestSessions.get(request) ?? sessionRevision();
    requireCurrentSession(revision);
    request.signal.throwIfAborted();
    const owner = ownedRequests.get(request);
    if (owner) requireSessionRequest(owner, request.signal);
    await surfaceForbidden(response, request, revision);

    requireCurrentSession(revision);
    if (response.status !== 401) return response;

    if (!owner) return response;
    requireSessionRequest(owner, request.signal);
    const path = new URL(request.url).pathname.slice(new URL(owner.base).pathname.length);
    if (/^\/auth\/(?:refresh|login|switch-tenant|register|forgot-password|reset-password|request-account|magic-login)(?:\/|$)/.test(path)) return response;
    const renewal = await renewSession(owner, request.headers.get('Accept-Language') ?? getLocaleSnapshot().locale);
    requireSessionRequest(owner, request.signal);
    if (renewal.kind === 'unconfirmed') return response;
    if (renewal.kind === 'refused') {
      useAuthStore.getState().clearAuth();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        const redirect = encodeURIComponent(window.location.href);
        window.location.replace('/login?redirect=' + redirect);
      }
      return response;
    }
    const newToken = renewal.token;

    // A 401 alone is not a durable receipt proving that an action had no
    // effects. Never resend a write or consume its body here. The caller gets
    // the original failure and decides whether to reconcile or offer retry.
    if (request.method !== 'GET' && request.method !== 'HEAD') return response;
    // These native GET endpoints generate and consume AI resources. Their
    // original result belongs to one user intent, just like a write.
    if (/^\/ai\/(?:insights|conversations\/[^/]+\/(?:summary|analysis))(?:\/|$)/.test(path)) return response;

    // Preserve cancellation and all request settings when retrying a read;
    // use the same configured transport as the original SDK request.
    const headers = new Headers(request.headers);
    headers.set('Authorization', `Bearer ${newToken}`);
    const retried = new Request(request, { headers });
    const transport = options.fetch ?? globalThis.fetch;
    const result = await transport(retried);
    requireSessionRequest(owner, request.signal);
    await surfaceForbidden(result, retried, revision);
    requireSessionRequest(owner, request.signal);
    return result;
  });
}

async function surfaceForbidden(response: Response, request: Request, revision: number) {
  if (response.status !== 403 || typeof window === 'undefined') return;
  try {
    const detail = getConversationForbiddenDetail(request.url, await response.clone().json());
    if (detail && revision === sessionRevision() && !request.signal.aborted) {
      window.dispatchEvent(new CustomEvent(INBOX_CONVERSATION_FORBIDDEN_EVENT, { detail }));
    }
  } catch { /* Non-JSON failures remain available to the caller. */ }
}

export { client };
